import { GridRowData, IInventoryItemVenue } from '@app/shared/types';
import { find, get, gt, isEqual, isNil, lt } from 'lodash';
import { DecimalNumber } from '@app/shared/common';
import { InitialFactorValueAndCount } from '@app/shared/types/entities/order/order-data.type';
import { getMeasureFromVendor } from '@shared/utils/inventory-item.utils';
import { IInventoryItemVenueVendor } from '@shared/types';
import { InventoryItemVenueMeasure } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import {
  MAX_INT_FOR_ORDER_QUANTITY,
  MIN_INT_FOR_ORDER_QUANTITY,
} from '@config/global-constants';

export const findCurrentFactorValue = (
  rowData,
  currentMeasureEntityId: string,
): number => {
  return (
    find(rowData.measures as InventoryItemVenueMeasure[], {
      entity_id: currentMeasureEntityId,
    })?.factor_value || 0
  );
};

export const findCurrentCost = (rowData, vendorEntityId: string): number => {
  const currentMeasure = getMeasureFromVendor(
    rowData.vendors as IInventoryItemVenueVendor[],
    vendorEntityId,
    rowData.measure_entity_id as string,
  );

  return new DecimalNumber(get(currentMeasure, 'vendor_cost')).valueOf();
};

export const handleOrderUnitChange = (
  currentRowData: GridRowData,
  vendorEntityId,
  maximum = MAX_INT_FOR_ORDER_QUANTITY,
  minimum = MIN_INT_FOR_ORDER_QUANTITY,
): GridRowData => {
  const currentCost: number = findCurrentCost(currentRowData, vendorEntityId);
  const currentMeasureEntityId: string =
    currentRowData.measure_entity_id as string;
  const currentFactorValue: number = findCurrentFactorValue(
    currentRowData,
    currentMeasureEntityId,
  );
  const count = get(currentRowData, 'initialFactorValueAndCount.count', null);
  const initFactorValue = get(
    currentRowData,
    'initialFactorValueAndCount.factorValue',
    0,
  );

  let initialCountOfRoundCeil: number | null = isNil(count)
    ? null
    : new DecimalNumber(initFactorValue || 0)
        .mul(count)
        .div(currentFactorValue)
        .ceil()
        .valueOf();
  if (gt(initialCountOfRoundCeil, maximum)) initialCountOfRoundCeil = maximum;
  if (lt(initialCountOfRoundCeil, minimum)) initialCountOfRoundCeil = minimum;

  return {
    ...currentRowData,
    item_cost: currentCost,
    count: isEqual(currentFactorValue, initFactorValue)
      ? count
      : initialCountOfRoundCeil,
  };
};

export const updateInitialFactorValueAndCount = (
  currentRowData: GridRowData,
): InitialFactorValueAndCount => {
  const { measure_entity_id: measureEntityId, count } = currentRowData;
  const currentFactorValue = findCurrentFactorValue(
    currentRowData,
    measureEntityId as string,
  );

  return {
    factorValue: currentFactorValue,
    count: count as string,
  };
};

export const appendInitialFactorValueAndCount = (
  item: IInventoryItemVenue,
): IInventoryItemVenue => {
  const factorValue = findCurrentFactorValue(item, item.measure_entity_id);

  return {
    ...item,
    initialFactorValueAndCount: {
      factorValue,
      count: get(item, 'count', undefined),
    },
  };
};
